import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { apiClient } from '../lib/apiClient';
import { searchLoadingState } from '../hooks/useSearch';
import { 
  QBRResponse, 
  QBRReportsResponse, 
  QBRStatus, 
  QBRCreate,
  CustomerProfile,
  EnhancedCustomersResponse,
  QBRTemplate
} from '../types/api';
import { 
  Plus, 
  Calendar,
  FileText,
  RefreshCw,
  Edit,
  Trash2,
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Users,
  ExternalLink,
  Eye,
  EyeOff,
  Settings
} from 'lucide-react';

const QBRPage: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId?: string }>();
  const [reports, setReports] = useState<QBRResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerProfile | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 20;

  // Load customers for the create modal
  const loadCustomers = useCallback(async () => {
    try {
      const response = await apiClient.getCustomers({ limit: 1000, offset: 0 });
      setCustomers(response.customers);
      
      // If customerId is provided, find and select that customer
      if (customerId) {
        const customer = response.customers.find((c: CustomerProfile) => c.customer_id === customerId);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  }, [customerId]);

  // Load QBR reports
  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        limit,
        offset: currentPage * limit
      };
      
      if (customerId) {
        params.customer_id = customerId;
      }
      
      const response = await apiClient.getQBRReports(params);
      
      setReports(response.reports);
      setTotalCount(response.pagination.total);
      setHasMore(response.pagination.has_more);
      
    } catch (err: any) {
      console.error('Failed to load QBR reports:', err);
      setError(err.response?.data?.error || 'Failed to load QBR reports');
    } finally {
      setLoading(false);
    }
  }, [currentPage, customerId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleCreateQBR = async (qbrData: QBRCreate) => {
    try {
      // Start loading animation
      searchLoadingState.incrementLoading();
      
      const response = await apiClient.createQBRReport(qbrData);
      setShowCreateModal(false);
      await loadReports(); // Refresh the list
      
      // DON'T navigate immediately - start polling for completion
      await pollForQBRCompletion(response.report_id, qbrData.customer_id);
      
    } catch (err: any) {
      console.error('Failed to create QBR:', err);
      setError(err.response?.data?.error || 'Failed to create QBR report');
      searchLoadingState.decrementLoading(); // Clear loading on error
    }
  };

  const pollForQBRCompletion = async (reportId: string, customerId: string) => {
    const maxAttempts = 60; // 5 minutes max (5 second intervals)
    let attempts = 0;
    
    const pollInterval = setInterval(async () => {
      attempts++;
      
      try {
        console.log(`[QBR] Polling attempt ${attempts}/${maxAttempts} for report ${reportId}`);
        
        // Check QBR status
        const statusResponse = await (apiClient as any).getQBRReportStatus(reportId);
        
        if (statusResponse.status === 'draft' || statusResponse.status === 'final') {
          // Report is ready! Navigate to it
          console.log(`[QBR] Report ${reportId} is ready, navigating to detail page`);
          clearInterval(pollInterval);
          searchLoadingState.decrementLoading(); // Clear loading animation
          
          navigate(`/customers/${customerId}/qbrs/${reportId}`, {
            state: { from: customerId ? `/customers/${customerId}/qbrs` : '/qbrs' }
          });
        } else if (statusResponse.status === 'generating') {
          // Still generating, continue polling
          console.log(`[QBR] Report ${reportId} still generating...`);
        } else {
          // Unknown status, stop polling
          console.log(`[QBR] Report ${reportId} has unknown status: ${statusResponse.status}`);
          clearInterval(pollInterval);
          searchLoadingState.decrementLoading();
          setError(`QBR report generation failed with status: ${statusResponse.status}`);
        }
        
      } catch (err: any) {
        console.error(`[QBR] Error polling report ${reportId}:`, err);
        
        if (attempts >= maxAttempts) {
          // Max attempts reached, stop polling
          clearInterval(pollInterval);
          searchLoadingState.decrementLoading();
          setError('QBR report generation timed out. Please try again.');
        }
      }
      
      // Stop polling after max attempts
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        searchLoadingState.decrementLoading();
        setError('QBR report generation timed out. Please try again.');
      }
      
    }, 5000); // Poll every 5 seconds
  };

  const handleDeleteQBR = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this QBR report?')) {
      return;
    }
    
    try {
      await apiClient.deleteQBRReport(reportId);
      await loadReports(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to delete QBR:', err);
      setError(err.response?.data?.error || 'Failed to delete QBR report');
    }
  };

  const handleRegenerateQBR = async (reportId: string) => {
    try {
      await apiClient.regenerateQBRReport(reportId);
      await loadReports(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to regenerate QBR:', err);
      setError(err.response?.data?.error || 'Failed to regenerate QBR report');
    }
  };

  const handleFinalizeQBR = async (reportId: string) => {
    try {
      await apiClient.finalizeQBRReport(reportId);
      await loadReports(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to finalize QBR:', err);
      setError(err.response?.data?.error || 'Failed to finalize QBR report');
    }
  };

  const getStatusBadge = (status: QBRStatus) => {
    const statusConfig = {
      draft: { color: 'bg-yellow-100 text-yellow-800', icon: Edit },
      generating: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      final: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      archived: { color: 'bg-gray-100 text-gray-800', icon: FileText }
    };
    
    const config = statusConfig[status];
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Quarterly Business Reviews
          </h1>
          <p className="text-gray-600 mt-2">
            AI-powered QBR reports for your customers
          </p>
          {selectedCustomer && (
            <div className="mt-2">
              <Link 
                to="/customers" 
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                ← Back to All Customers
              </Link>
              <span className="text-gray-400 mx-2">•</span>
              <span className="text-sm text-gray-600">
                Showing QBRs for {selectedCustomer.name}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex gap-3">
          <Button 
            onClick={() => navigate('/qbr-templates')}
            variant="outline"
            className="border-gray-300 hover:bg-gray-50"
          >
            <Settings className="w-4 h-4 mr-2" />
            Manage Templates
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate New QBR
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <div className="text-red-800">{error}</div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            QBR Reports
            {totalCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalCount} total
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reports.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No QBR Reports Yet
              </h3>
              <p className="text-gray-600 mb-6">
                Generate your first AI-powered Quarterly Business Review to get started.
              </p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate New QBR
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Quarter</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.report_id}>
                      <TableCell>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="font-medium">{report.customer_name}</div>
                            <div className="text-sm text-gray-500">
                              {report.value_realized_quotes?.length || 0} quotes
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          {formatDateRange(report.start_date, report.end_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(report.status)}
                      </TableCell>
                      <TableCell>
                        {report.generated_at ? (
                          <div className="text-sm text-gray-600">
                            {formatDate(report.generated_at)}
                          </div>
                        ) : (
                          <span className="text-gray-400">Pending</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">
                          {formatDate(report.created_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/customers/${report.customer_id}/qbrs/${report.report_id}`, {
                              state: { from: customerId ? `/customers/${customerId}/qbrs` : '/qbrs' }
                            })}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {report.status === 'generating' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRegenerateQBR(report.report_id)}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {report.status === 'draft' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleFinalizeQBR(report.report_id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteQBR(report.report_id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(hasMore || currentPage > 0) && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-600">
                    Showing {reports.length} of {totalCount} reports
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!hasMore}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create QBR Modal */}
      {showCreateModal && (
        <CreateQBRModal
          customers={customers}
          selectedCustomer={selectedCustomer}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateQBR}
        />
      )}
    </div>
  );
};

// Create QBR Modal Component
interface CreateQBRModalProps {
  customers: CustomerProfile[];
  selectedCustomer: CustomerProfile | null;
  onClose: () => void;
  onSubmit: (data: QBRCreate) => void;
}

const CreateQBRModal: React.FC<CreateQBRModalProps> = ({
  customers,
  selectedCustomer,
  onClose,
  onSubmit
}) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState(selectedCustomer?.customer_id || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<QBRTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Set default date range (last quarter)
  useEffect(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Calculate last quarter
    let quarterStartMonth, quarterEndMonth, quarterYear;
    
    if (currentMonth >= 0 && currentMonth <= 2) {
      // Q1 - previous year Q4
      quarterStartMonth = 9; // October
      quarterEndMonth = 11; // December
      quarterYear = currentYear - 1;
    } else if (currentMonth >= 3 && currentMonth <= 5) {
      // Q2 - previous Q1
      quarterStartMonth = 0; // January
      quarterEndMonth = 2; // March
      quarterYear = currentYear;
    } else if (currentMonth >= 6 && currentMonth <= 8) {
      // Q3 - previous Q2
      quarterStartMonth = 3; // April
      quarterEndMonth = 5; // June
      quarterYear = currentYear;
    } else {
      // Q4 - previous Q3
      quarterStartMonth = 6; // July
      quarterEndMonth = 8; // September
      quarterYear = currentYear;
    }
    
    const start = new Date(quarterYear, quarterStartMonth, 1);
    const end = new Date(quarterYear, quarterEndMonth + 1, 0); // Last day of month
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const response = await apiClient.getQBRTemplates();
        setTemplates(response.templates || []);
      } catch (err) {
        console.error('Failed to load templates:', err);
      } finally {
        setTemplatesLoading(false);
      }
    };
    
    loadTemplates();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCustomerId || !startDate || !endDate) {
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit({
        customer_id: selectedCustomerId,
        start_date: startDate,
        end_date: endDate
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md shadow-xl">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Generate New QBR</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Customer
            </label>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              required
            >
              <option value="">Select a customer</option>
              {customers.map((customer) => (
                <option key={customer.customer_id} value={customer.customer_id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              PowerPoint Template (Optional)
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              disabled={templatesLoading}
            >
              <option value="">Default Template</option>
              {templates.map((template) => (
                <option key={template.template_id} value={template.template_id}>
                  {template.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Select a custom template for PDF export. {templates.length === 0 && !templatesLoading && (
                <Link to="/qbr-templates" className="text-blue-600 hover:underline">
                  Upload templates
                </Link>
              )}
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Generate QBR
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QBRPage;
